import * as Immutable from 'immutable';
import * as React from 'react';
import { WrappedComponentProps as InjectedIntlProps } from 'react-intl';
import { Link } from 'react-router-dom';

import { HELP_CENTER_URL } from '../../shared/constants';
import { addModifiers } from '../../shared/helpers';
import { getIsInternalLink } from '../Learn/LearnBlocks/BlockText';
import { FlashMessageLinkHandlers } from './FlashMessages';

interface ClickableMessageProps {
  message: Immutable.Map<string, any>;
  link: Immutable.Map<string, any>;
  messageVariables: boolean;
  InputType: string;
  props?: Immutable.Map<string, any>;
  modifiers?: string[];
  excludedLinks?: Immutable.List<number>;
  linkHandlers?: FlashMessageLinkHandlers;
}

export const convertToLink = (
  message: any,
  keys: any,
  urls: string[],
  InputType: any = 'link',
  excludedLinks: Immutable.List<number> = Immutable.List(),
  props?: any,
  linkHandlers?: FlashMessageLinkHandlers
) => {
  const regex = new RegExp(`\\b(${keys.join('|')})\\b`);
  let linksCount = 0;

  return (
    <div>
      {message.split(regex).reduce((prev: any, current: any, i: any) => {
        if (keys.includes(current) && excludedLinks.includes(linksCount) === false) {
          const index = keys.findIndex((el: any) => el === current);
          let Comp = InputType;
          if (InputType === 'link') {
            if (!props) {
              props = Immutable.fromJS({});
            }
            const isInternalLink = getIsInternalLink(urls[index]);
            if (isInternalLink) {
              props = props.set('to', urls[index]);
              if (urls[index] === '/contact-us') {
                props = props.set('target', '_blank').set('to', { pathname: HELP_CENTER_URL });
              }
              Comp = Link;
            } else {
              props = props.set('href', urls[index]).set('target', '_blank');
              Comp = 'a' as keyof JSX.IntrinsicElements;
            }
          }
          const classLink = typeof InputType === 'string' ? `flash-messages-input--${InputType}` : 'flash-messages-input';
          linksCount = linksCount + 1;

          // Resolve `linkHandler` (a string key on `props`) to an actual onClick
          // function provided via the `linkHandlers` prop on the parent
          // FlashMessages container. Functions cannot be safely round-tripped
          // through redux/Immutable, so we reference handlers by name instead.
          const finalProps = props ? props.toJS() : {};
          if (finalProps.linkHandler && linkHandlers && typeof linkHandlers[finalProps.linkHandler] === 'function') {
            finalProps.onClick = linkHandlers[finalProps.linkHandler];
          }
          delete finalProps.linkHandler;

          return prev.concat(
            <Comp className={classLink} key={`flash-message-link-${i}-${index}`} {...finalProps}>
              {keys[index]}
            </Comp>
          );
        } else if (keys.includes(current)) {
          linksCount = linksCount + 1;

          return prev.concat([current]);
        } else {
          return prev.concat([current]);
        }
      }, [])}
    </div>
  );
};

export const ClickableMessage = ({
  message,
  link,
  intl,
  messageVariables,
  InputType,
  modifiers = [],
  excludedLinks = Immutable.List(),
  props,
  linkHandlers,
}: ClickableMessageProps & InjectedIntlProps) => {
  let intlValues = {};
  if (messageVariables) {
    intlValues = message.get('intlValues') ? message.get('intlValues').toJS() : {};
  }
  const intlMsg = intl.formatMessage({ id: message.get('msg'), defaultMessage: message.get('defaultMessage') }, intlValues);
  const urls = link.get('urls');
  const keys = link
    .get('keys')
    // @ts-ignore
    .map((key: any, index: number) => intl.formatMessage({ id: key, defaultMessage: link.getIn(['defaultMsgkeys', index]) }));

  return (
    <li className={addModifiers('flash-messages__item', modifiers)}>
      <div className="flash-messages__container">
        {convertToLink(intlMsg, keys.toJS(), urls.toJS(), InputType, excludedLinks, props, linkHandlers)}
      </div>
    </li>
  );
};
